<template>
    <div class="apply-note-process-modal">
        <ProcessModal
            :is-open="isOpen"
            :title="$t('funnel.campaign.apply.note.title')"
            :summary-text="$t('funnel.campaign.apply.note.summary')"
            show-save
            :process-tip-text="$t('funnel.campaign.apply.note.tip')"
            :process-tip-link="$t('funnel.campaign.apply.note.more.link')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <div class="merge-field-container">
                    <CampaignBuilderMergeableTextArea
                        v-model="applyNoteTitle"
                        data-qa="apply-note-title-input"
                        :has-error="invalidApplyNoteTitle"
                        :has-legacy-format="true"
                        :funnel-id="funnelId"
                        :default-place-holder="invalidApplyNoteTitle ? $t('funnel.campaign.apply.note.required') : $t('funnel.campaign.apply.note.title.text')"
                        :merge-field-categories="mergeFields"
                        class="apply-note-merge-field-input"
                    />
                </div>
                <DsMultiselect
                    v-model="applyNoteType"
                    data-qa="apply-note-type"
                    class="apply-note-type"
                    :placeholder="$t('funnel.campaign.apply.note.action.type.text')"
                    :force-invalid="invalidApplyNoteType"
                    :options="applyNoteTypeOptions"
                    bind-value-only
                    required
                >
                    <template #help>
                        <span v-if="invalidApplyNoteType" data-qa="invalid-applyNote-type">
                            {{ $t('funnel.campaign.apply.note.required') }}
                        </span>
                    </template>
                </DsMultiselect>
                <div class="merge-field-container">
                    <CampaignBuilderMergeableTextArea
                        v-model="applyNoteBody"
                        data-qa="apply-note-description"
                        :has-legacy-format="true"
                        :funnel-id="funnelId"
                        :default-place-holder="$t('funnel.campaign.apply.note.description.text')"
                        required
                        :merge-field-categories="mergeFields"
                        class="apply-note-merge-field-input"
                    >
                        <template #error>
                            {{ $t('funnel.campaign.apply.note.required') }}
                        </template>
                    </CampaignBuilderMergeableTextArea>
                </div>
                <DsMultiselect
                    v-model="applyNoteNotify"
                    data-qa="apply-note-notify"
                    :placeholder="$t('funnel.campaign.apply.note.notify.users.text')"
                    bind-value-only
                    multiple
                    searchable
                    :options="usersOptions"
                />
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import CampaignBuilderMergeableTextArea from '../../../whens/campaignBuilderMergeableTextArea/CampaignBuilderMergeableTextArea.vue';
import {
    getContactActionOptions,
    getGroupMembers,
    getMergeFieldsForCB,
} from '../../../../configuration/task.flow.api';

import {
    initializeCell,
    saveCell,
    getApplyNote,
} from './note.flow.api';

export default {
    components: {
        ProcessModal,
        CampaignBuilderMergeableTextArea,
    },
    props: {
        funnelId: {
            type: Number,
            default: 0,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    marketingNoteId: null,
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            mergeFields: [],
            marketingNoteId: 0,
            applyNoteType: '',
            applyNoteTitle: '',
            applyNoteBody: '',
            applyNoteNotify: [],
            applyNoteTypeOptions: [],
            usersOptions: [],
            invalidApplyNoteTitle: false,
            invalidApplyNoteType: false,
        };
    },

    async mounted() {
        await this.fetchModalOptions();
        await this.loadConfig();
    },

    methods: {
        async fetchModalOptions() {
            this.applyNoteTypeOptions = await getContactActionOptions();
            this.usersOptions = await getGroupMembers();
            this.mergeFields = await getMergeFieldsForCB(this.funnelId);
        },

        async loadConfig() {
            const { value } = this.cell;

            if (value?.marketingNoteId) {
                const applyNoteValues = await getApplyNote(value.marketingNoteId);

                if (typeof value.marketingNoteId === 'string' || value.marketingNoteId instanceof String) {
                    this.marketingNoteId = value.marketingNoteId.replace('L', '');
                } else {
                    this.marketingNoteId = `${value.marketingNoteId}`;
                }

                this.applyNoteType = applyNoteValues.actionType || '';
                this.applyNoteTitle = applyNoteValues.actionDescription || '';
                this.applyNoteBody = applyNoteValues.creationNotes || '';
                this.applyNoteNotify = applyNoteValues.notifyUsers?.replace('L', '').split(',') || [];
            } else {
                this.marketingNoteId = await initializeCell(value?.name);
            }
        },

        closeModal() {
            this.$emit('close-modal');
        },

        async updateConfig() {
            if (this.isValid()) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.marketingNoteId = `${this.marketingNoteId}L`;

                await saveCell(cell.value.marketingNoteId, this.applyNoteType, this.applyNoteTitle, this.applyNoteBody, '0', this.applyNoteNotify.toString());

                this.$emit('update-cell-value', { cell });
            }
        },

        setIsValidApplyNoteTitle() {
            this.invalidApplyNoteTitle = this.applyNoteTitle === '';
        },

        setIsValidApplyNoteType() {
            this.invalidApplyNoteType = this.applyNoteType === '';
        },


        isValid() {
            this.setIsValidApplyNoteTitle();
            this.setIsValidApplyNoteType();

            return !this.invalidApplyNoteTitle && !this.invalidApplyNoteType;
        },
    },
};
</script>

<style lang="scss" scoped>
.apply-note-merge-field-input {
    width: -webkit-fill-available;
    max-width: px-to-rem(590);
}
.merge-field-container {
    display: flex;
}

.merge-field-icon {
    color: $color-blue-700;
}

</style>
<style lang="scss">
.apply-note-process-modal .apply-note-type .multiselect-single label {
    padding: 0;
}
</style>
